import React, { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import API from "../../services/api";
import totalAssureLogo from "../../assets/totalassure.png";
import banner from "../../assets/banner.png";

/* ─── Icons (UNCHANGED) ─── */
const IconMail     = () => (<svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m2 7 10 7 10-7"/></svg>);
const IconArrow    = () => (<svg width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24"><path d="M5 12h14M12 5l7 7-7 7"/></svg>);
const IconArrowLeft= () => (<svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>);
const IconLock     = () => (<svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>);
const IconCheck    = () => (<svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path d="m5 13 4 4L19 7"/></svg>);
const IconEyeOff   = () => (<svg width="17" height="17" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>);
const IconEye      = () => (<svg width="17" height="17" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M1.1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>);

/* ─── Step pill (UNCHANGED) ─── */
const StepPill = ({ num, label, active, done }) => (
  <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:4 }}>
    <div style={{
      width:34, height:34, borderRadius:"50%",
      border: `2px solid ${done ? "#f97316" : active ? "#f97316" : "rgba(255,255,255,0.2)"}`,
      background: done ? "#f97316" : active ? "rgba(249,115,22,0.15)" : "rgba(255,255,255,0.05)",
      display:"flex", alignItems:"center", justifyContent:"center",
      fontSize:12, fontWeight:700,
      color: done ? "#fff" : active ? "#f97316" : "rgba(255,255,255,0.3)",
      transition:"all 0.3s",
      backdropFilter:"blur(4px)",
    }}>
      {done ? <IconCheck /> : num}
    </div>
    <span style={{
      fontSize:9, fontWeight:700, letterSpacing:"0.06em", textTransform:"uppercase",
      color: active||done ? "#f97316" : "rgba(255,255,255,0.3)",
      transition:"color 0.3s",
    }}>{label}</span>
  </div>
);

/* ─── Main Component ─── */
function ForgotPassword() {
  const navigate = useNavigate();

  /* ── State ── */
  const [email,       setEmail]       = useState("");
  const [loading,     setLoading]     = useState(false);
  const [errorMsg,    setErrorMsg]    = useState("");
  const [successMsg,  setSuccessMsg]  = useState("");
  const [otp,         setOtp]         = useState(new Array(6).fill(""));
  const [newPassword, setNewPassword] = useState("");
  const [showPwd,     setShowPwd]     = useState(false);

  // step: 1 = email, 2 = verify OTP, 3 = set new password, 4 = done
  const [step, setStep] = useState(1);

  const inputRefs = useRef([]);

  /* ── Handlers ── */

  // Step 1: Send OTP
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    setErrorMsg("");
    try {
      const response = await API.post("/auth/forgot-password", { email });
      if (response.data.success) setStep(2);
    } catch (err) {
      setErrorMsg(err.response?.data?.message || "Communication failure with recovery systems.");
    } finally {
      setLoading(false);
    }
  };

  // OTP input helpers (UNCHANGED)
  const handleOtpChange = (element, index) => {
    if (isNaN(element.value)) return false;
    const updatedOtp = [...otp];
    updatedOtp[index] = element.value;
    setOtp(updatedOtp);
    if (element.value !== "" && inputRefs.current[index + 1]) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && inputRefs.current[index - 1]) {
      inputRefs.current[index - 1].focus();
    }
  };

  // Step 2: Verify OTP only
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    setErrorMsg("");
    const compiledOtp = otp.join("");
    if (compiledOtp.length !== 6) {
      setErrorMsg("Please complete the full 6-digit access code sequence.");
      setLoading(false);
      return;
    }
    try {
      const response = await API.post("/auth/verify-otp", { email, otp: compiledOtp });
      if (response.data.success) setStep(3);
    } catch (err) {
      setErrorMsg(err.response?.data?.message || "Invalid or expired authorization code.");
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Reset password
  const handleResetPasswordSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    setErrorMsg("");
    setSuccessMsg("");
    try {
      const response = await API.post("/auth/reset-password", {
        email,
        otp: otp.join(""),
        newPassword,
      });
      if (response.data.success) {
        setSuccessMsg("Password updated successfully! Redirecting to login...");
        setStep(4);
        setTimeout(() => navigate("/login"), 2200);
      }
    } catch (err) {
      setErrorMsg(err.response?.data?.message || "Failed to reset password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Resend OTP — go back to step 1, clear OTP
  const handleResend = () => {
    setStep(1);
    setOtp(new Array(6).fill(""));
    setErrorMsg("");
    setSuccessMsg("");
  };

  return (
    <>
      <style>{`
     @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800;900&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        .fp-root {
          min-height: 100vh; width: 100%; font-family: 'Montserrat', sans-serif;
          position: relative; display: flex; align-items: center;
          justify-content: center; overflow: hidden;
        }
        .fp-bg-img {
          position: fixed; inset: 0; width: 100%; height: 100%;
          object-fit: cover; object-position: center; z-index: 0;
        }
        .fp-bg-overlay {
          position: fixed; inset: 0; z-index: 1;
          background: linear-gradient(135deg,
            rgba(10,4,24,0.82) 0%, rgba(30,12,60,0.75) 40%,
            rgba(92,45,145,0.45) 70%, rgba(200,16,46,0.25) 100%);
        }
        .fp-vignette {
          position: fixed; inset: 0; z-index: 2;
          background: radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.55) 100%);
          pointer-events: none;
        }
        .fp-center {
          position: relative; z-index: 10; width: 100%; max-width: 480px;
          padding: 24px 20px; display: flex; flex-direction: column;
          align-items: center; gap: 0;
        }
        .fp-topbar {
          display: flex; align-items: center; justify-content: space-between;
          width: 100%; margin-bottom: 28px; padding: 0 4px;
        }
        .fp-topbar-logo { height: 32px; object-fit: contain; filter: brightness(0) invert(1); opacity: 0.9; }
        .fp-topbar-ap   { height: 36px; object-fit: contain; max-width: 110px; filter: brightness(0) invert(1); opacity: 0.85; }

        .fp-steps {
          display: flex; align-items: flex-start; gap: 0;
          width: 100%; max-width: 320px; margin-bottom: 20px;
        }
        .fp-step-connector { flex: 1; height: 1.5px; background: rgba(255,255,255,0.15); margin-top: 17px; }
        .fp-step-connector.done { background: rgba(249,115,22,0.5); }

        .fp-card {
          width: 100%; background: rgba(255,255,255,0.97); border-radius: 24px;
          overflow: hidden;
          box-shadow: 0 32px 80px rgba(0,0,0,0.45), 0 0 0 1px rgba(255,255,255,0.12);
          backdrop-filter: blur(24px);
        }
        .fp-card-accent {
          height: 4px;
          background: linear-gradient(90deg, #5C2D91, #f97316, #C8102E);
          background-size: 200% 100%;
          animation: fp-accent-shift 4s ease infinite;
        }
        @keyframes fp-accent-shift {
          0%   { background-position: 0% 0%;   }
          50%  { background-position: 100% 0%; }
          100% { background-position: 0% 0%;   }
        }
        .fp-card-body { padding: 32px 36px 28px; }

        .fp-heading { font-size: 22px; font-weight: 800; color: #0f172a; letter-spacing: -0.03em; margin-bottom: 5px; line-height: 1.2; }
        .fp-sub { font-size: 13px; color: #64748b; line-height: 1.55; margin-bottom: 24px; }
        .fp-sub strong { color: #0f172a; font-weight: 600; }

        .fp-alert { display: flex; align-items: flex-start; gap: 9px; border-radius: 10px; padding: 11px 13px; margin-bottom: 18px; font-size: 13px; font-weight: 500; line-height: 1.5; }
        .fp-alert.error   { background:#fef2f2; border:1px solid #fecaca; color:#dc2626; }
        .fp-alert.success { background:#f0fdf4; border:1px solid #bbf7d0; color:#15803d; }

        .fp-input-group { display: flex; flex-direction: column; gap: 5px; margin-bottom: 14px; }
        .fp-label { font-size: 11.5px; font-weight: 700; color: #475569; letter-spacing: 0.04em; text-transform: uppercase; }
        .fp-input-wrap {
          display: flex; align-items: center; gap: 10px; height: 48px;
          border-radius: 12px; border: 1.5px solid #e2e8f0; background: #f8fafc;
          padding: 0 14px; transition: all 0.18s;
        }
        .fp-input-wrap:focus-within { border-color: #f97316; background: #fffaf7; box-shadow: 0 0 0 3px rgba(249,115,22,0.1); }
        .fp-icon { color: #94a3b8; display: flex; align-items: center; flex-shrink: 0; }
      .fp-input { flex: 1; background: transparent; border: none; outline: none; font-size: 14px; font-family: 'Montserrat', sans-serif; color: #0f172a; }
        .fp-input::placeholder { color: #94a3b8; }
        .fp-eye { background: none; border: none; cursor: pointer; color: #94a3b8; display: flex; align-items: center; padding: 0; flex-shrink: 0; transition: color 0.15s; }
        .fp-eye:hover { color: #64748b; }

        .fp-otp-section { margin-bottom: 18px; }
        .fp-otp-label { font-size: 11.5px; font-weight: 700; color: #475569; letter-spacing: 0.04em; text-transform: uppercase; display: block; margin-bottom: 12px; }
        .fp-otp-row { display: flex; justify-content: center; gap: 10px; }
        .fp-otp-box {
          width: 44px; height: 52px; flex: 0 0 44px; text-align: center;
          font-size: 20px; font-weight: 800; color: #0f172a; background: #f8fafc;
          border: 2px solid #e2e8f0; border-radius: 12px; outline: none;
        transition: all 0.18s; font-family: 'Montserrat', sans-serif;
          caret-color: #f97316; padding: 0;
        }
        .fp-otp-box:focus { border-color: #f97316; background: #fffaf7; box-shadow: 0 0 0 3px rgba(249,115,22,0.12); transform: translateY(-1px); }
        .fp-otp-box:not(:placeholder-shown) { background: #fff7ed; border-color: #fdba74; color: #c2410c; }
        .fp-otp-box:nth-child(3) { margin-right: 6px; }

        .fp-info {
          display: flex; gap: 9px; align-items: flex-start;
          background: #fffbeb; border: 1px solid #fde68a; border-radius: 10px;
          padding: 11px 13px; font-size: 12px; color: #78350f; line-height: 1.55; margin-bottom: 18px;
        }

        .fp-btn {
          display: flex; align-items: center; justify-content: center; gap: 8px;
          width: 100%; height: 50px; border-radius: 13px; border: none;
          background: linear-gradient(135deg, #f97316, #ea580c); color: #fff;
        font-size: 14px; font-family: 'Montserrat', sans-serif; font-weight: 700;
          cursor: pointer; letter-spacing: 0.01em;
          box-shadow: 0 6px 24px rgba(249,115,22,0.38); transition: all 0.2s;
        }
        .fp-btn:hover:not(:disabled) { background: linear-gradient(135deg, #fb923c, #f97316); box-shadow: 0 10px 32px rgba(249,115,22,0.48); transform: translateY(-2px); }
        .fp-btn:active:not(:disabled) { transform: translateY(0); }
        .fp-btn:disabled { opacity: 0.55; cursor: not-allowed; transform: none; }

        .fp-ghost {
          display: flex; align-items: center; justify-content: center; gap: 7px;
          width: 100%; height: 44px; border-radius: 11px; background: transparent;
          border: 1.5px solid #e2e8f0; color: #64748b; font-size: 13px;
        font-family: 'Montserrat', sans-serif; font-weight: 600; cursor: pointer;
          text-decoration: none; transition: all 0.18s; margin-top: 10px;
        }
        .fp-ghost:hover { border-color: #f97316; color: #f97316; background: #fff7ed; }

        .fp-divider { display: flex; align-items: center; gap: 10px; margin: 16px 0; color: #cbd5e1; font-size: 11px; font-weight: 600; letter-spacing: 0.05em; text-transform: uppercase; }
        .fp-divider-line { flex: 1; height: 1px; background: #f1f5f9; }

        .fp-card-footer { padding: 14px 36px 20px; border-top: 1px solid #f1f5f9; display: flex; align-items: center; justify-content: center; gap: 6px; font-size: 11px; color: #94a3b8; }
        .fp-card-footer svg { opacity: 0.6; }
        .fp-tagline { margin-top: 20px; text-align: center; font-size: 11px; color: rgba(255,255,255,0.35); letter-spacing: 0.04em; font-weight: 500; }

        @keyframes fp-spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        .fp-spin { animation: fp-spin 0.75s linear infinite; }

        @media (max-width: 540px) {
          .fp-center { padding: 20px 16px; }
          .fp-card-body { padding: 26px 22px 22px; }
          .fp-card-footer { padding: 12px 22px 16px; }
          .fp-heading { font-size: 19px; }
          .fp-otp-box { width: 40px; height: 48px; flex: 0 0 40px; font-size: 18px; }
          .fp-otp-row { gap: 8px; }
          .fp-topbar-logo { height: 26px; }
          .fp-topbar-ap { height: 28px; max-width: 90px; }
          .fp-steps { max-width: 280px; }
        }
        @media (max-width: 380px) {
          .fp-otp-box { width: 36px; height: 44px; flex: 0 0 36px; font-size: 16px; border-radius: 9px; }
          .fp-otp-row { gap: 6px; }
          .fp-card-body { padding: 22px 16px 18px; }
        }
        @media (prefers-reduced-motion: reduce) {
          .fp-card-accent { animation: none; }
          .fp-btn { transition: none; }
        }
      `}</style>

      <div className="fp-root">
        <img src={banner} alt="" className="fp-bg-img" />
        <div className="fp-bg-overlay" />
        <div className="fp-vignette" />

        <div className="fp-center">
          <div className="fp-topbar">
            <img src={totalAssureLogo} alt="Total Assure" className="fp-topbar-logo" />
            <img src={require("../../assets/AsianPaints Logo.jpeg")} alt="Asian Paints" className="fp-topbar-ap" />
          </div>

          {/* Step pills — now driven by step 1/2/3/4 */}
          <div className="fp-steps">
            <StepPill num="1" label="Email"  active={step === 1} done={step > 1} />
            <div className={`fp-step-connector${step > 1 ? " done" : ""}`} />
            <StepPill num="2" label="Verify" active={step === 2} done={step > 2} />
            <div className={`fp-step-connector${step > 2 ? " done" : ""}`} />
            <StepPill num="3" label="Reset"  active={step === 3 || step === 4} done={step > 3} />
          </div>

          <div className="fp-card">
            <div className="fp-card-accent" />

            <div className="fp-card-body">
              <AnimatePresence mode="wait">

                {/* ── STEP 1: Email ── */}
                {step === 1 && (
                  <motion.div key="step1"
                    initial={{ opacity:0, y:14 }} animate={{ opacity:1, y:0 }}
                    exit={{ opacity:0, y:-14 }} transition={{ duration:0.24, ease:"easeOut" }}>

                    <h2 className="fp-heading">Forgot your password?</h2>
                    <p className="fp-sub">Enter your registered email and we'll send a 6-digit code to verify it's you.</p>

                    {errorMsg && (
                      <div className="fp-alert error">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{flexShrink:0,marginTop:1}}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                        {errorMsg}
                      </div>
                    )}

                    <form onSubmit={handleSubmit}>
                      <div className="fp-input-group">
                        <label className="fp-label">Email address</label>
                        <div className="fp-input-wrap">
                          <span className="fp-icon"><IconMail /></span>
                          <input className="fp-input" type="email" value={email}
                            onChange={e => setEmail(e.target.value)}
                            placeholder="you@company.com" required autoComplete="email" />
                        </div>
                      </div>

                      <div className="fp-info">
                        <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{flexShrink:0,marginTop:1,color:"#d97706"}}><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></svg>
                        A 6-digit code will be sent to your email. Valid for <strong style={{marginLeft:3}}>10 minutes</strong>.
                      </div>

                      <button type="submit" className="fp-btn" disabled={loading}>
                        {loading
                          ? <><svg className="fp-spin" width="18" height="18" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.3)" strokeWidth="3.5"/><path d="M12 2a10 10 0 0 1 10 10" stroke="#fff" strokeWidth="3.5" strokeLinecap="round"/></svg>Sending code…</>
                          : <>Send OTP <IconArrow /></>}
                      </button>
                    </form>

                    <div className="fp-divider"><span className="fp-divider-line" />Remember it?<span className="fp-divider-line" /></div>
                    <Link to="/login" className="fp-ghost"><IconArrowLeft />Back to Login</Link>
                  </motion.div>
                )}

                {/* ── STEP 2: Verify OTP ── */}
                {step === 2 && (
                  <motion.div key="step2"
                    initial={{ opacity:0, y:14 }} animate={{ opacity:1, y:0 }}
                    exit={{ opacity:0, y:-14 }} transition={{ duration:0.24, ease:"easeOut" }}>

                    <h2 className="fp-heading">Enter your OTP</h2>
                    <p className="fp-sub">Code sent to <strong>{email}</strong>. Enter the 6-digit code below.</p>

                    {errorMsg && (
                      <div className="fp-alert error">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{flexShrink:0,marginTop:1}}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                        {errorMsg}
                      </div>
                    )}

                    <form onSubmit={handleVerifyOtp}>
                      <div className="fp-otp-section">
                        <label className="fp-otp-label">One-time password</label>
                        <div className="fp-otp-row">
                          {otp.map((data, index) => (
                            <input
                              key={index}
                              type="text"
                              inputMode="numeric"
                              maxLength="1"
                              ref={el => (inputRefs.current[index] = el)}
                              value={data}
                              onChange={e => handleOtpChange(e.target, index)}
                              onKeyDown={e => handleKeyDown(e, index)}
                              onFocus={e => e.target.select()}
                              className="fp-otp-box"
                              placeholder="·"
                              required
                            />
                          ))}
                        </div>
                      </div>

                      <button type="submit" className="fp-btn" disabled={loading}>
                        {loading
                          ? <><svg className="fp-spin" width="18" height="18" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.3)" strokeWidth="3.5"/><path d="M12 2a10 10 0 0 1 10 10" stroke="#fff" strokeWidth="3.5" strokeLinecap="round"/></svg>Verifying…</>
                          : <>Verify OTP <IconArrow /></>}
                      </button>

                      <p style={{marginTop:14, fontSize:12, color:"#94a3b8", textAlign:"center"}}>
                        Didn't receive it?{" "}
                        <button type="button" onClick={handleResend}
                        style={{background:"none", border:"none", cursor:"pointer", fontSize:12, fontWeight:700, color:"#f97316", fontFamily:"'Montserrat',sans-serif"}}>
                          Resend OTP
                        </button>
                      </p>
                    </form>

                    <Link to="/login" className="fp-ghost"><IconArrowLeft />Back to Login</Link>
                  </motion.div>
                )}

                {/* ── STEP 3: New Password ── */}
                {step === 3 && (
                  <motion.div key="step3"
                    initial={{ opacity:0, y:14 }} animate={{ opacity:1, y:0 }}
                    exit={{ opacity:0, y:-14 }} transition={{ duration:0.24, ease:"easeOut" }}>

                    <h2 className="fp-heading">Set new password</h2>
                    <p className="fp-sub">OTP verified. Create a new strong password for your account.</p>

                    {errorMsg && (
                      <div className="fp-alert error">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{flexShrink:0,marginTop:1}}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                        {errorMsg}
                      </div>
                    )}

                    <form onSubmit={handleResetPasswordSubmit}>
                      <div className="fp-input-group">
                        <label className="fp-label">New password</label>
                        <div className="fp-input-wrap">
                          <span className="fp-icon"><IconLock /></span>
                          <input className="fp-input"
                            type={showPwd ? "text" : "password"}
                            value={newPassword}
                            onChange={e => setNewPassword(e.target.value)}
                            placeholder="Create a strong password"
                            required />
                          <button type="button" className="fp-eye" onClick={() => setShowPwd(p => !p)}>
                            {showPwd ? <IconEyeOff /> : <IconEye />}
                          </button>
                        </div>
                      </div>

                      <button type="submit" className="fp-btn" disabled={loading}>
                        {loading
                          ? <><svg className="fp-spin" width="18" height="18" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.3)" strokeWidth="3.5"/><path d="M12 2a10 10 0 0 1 10 10" stroke="#fff" strokeWidth="3.5" strokeLinecap="round"/></svg>Updating…</>
                          : <>Reset Password <IconArrow /></>}
                      </button>
                    </form>

                    <Link to="/login" className="fp-ghost" style={{marginTop:10}}><IconArrowLeft />Back to Login</Link>
                  </motion.div>
                )}

                {/* ── STEP 4: Done ── */}
                {step === 4 && (
                  <motion.div key="step4"
                    initial={{ opacity:0, y:14 }} animate={{ opacity:1, y:0 }}
                    exit={{ opacity:0, y:-14 }} transition={{ duration:0.24, ease:"easeOut" }}>

                    <h2 className="fp-heading">All done! 🎉</h2>
                    <p className="fp-sub">Your password has been updated.</p>

                    {successMsg && (
                      <div className="fp-alert success">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{flexShrink:0,marginTop:1}}><path d="m5 13 4 4L19 7"/></svg>
                        {successMsg}
                      </div>
                    )}

                    <Link to="/login" className="fp-ghost" style={{marginTop:0}}><IconArrowLeft />Back to Login</Link>
                  </motion.div>
                )}

              </AnimatePresence>
            </div>

            <div className="fp-card-footer">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
              256-bit SSL · Asian Paints Enterprise
            </div>
          </div>

          <p className="fp-tagline">© Asian Paints Ltd · Secured Portal</p>
        </div>
      </div>
    </>
  );
}

export default ForgotPassword;